import { getApiClient } from './client'
import type {
  SeriesMoveResponseDto,
  SeriesMoveListResponseDto,
  CreateSeriesMoveDto,
} from './generated/api'

/**
 * Series-Moves API Facade
 *
 * Type-safe wrapper around the generated API client for series-moves operations.
 * Handles adding/removing moves from series.
 */

/**
 * Fetch all series-move relationships with optional filters
 *
 * @param params - Query parameters for filtering
 * @returns Promise resolving to list of series-move relationships
 */
export async function fetchSeriesMoves(params?: {
  seriesId?: string
  moveId?: string
  includeRelations?: boolean
  limit?: number
  offset?: number
}): Promise<SeriesMoveListResponseDto> {
  const response = await getApiClient().api.seriesMovesControllerFindAll({
    seriesId: params?.seriesId,
    moveId: params?.moveId,
    includeRelations: params?.includeRelations,
    limit: params?.limit,
    offset: params?.offset,
  })

  return response.data
}

/**
 * Fetch a single series-move relationship by ID
 *
 * @param id - Series-move relationship ID (UUID)
 * @returns Promise resolving to series-move details
 */
export async function fetchSeriesMove(
  id: string
): Promise<SeriesMoveResponseDto> {
  const response = await getApiClient().api.seriesMovesControllerFindById(id)

  return response.data
}

/**
 * Get all moves in a specific series
 *
 * @param seriesId - Series ID (UUID)
 * @returns Promise resolving to list of moves in the series
 */
export async function fetchMovesInSeries(
  seriesId: string
): Promise<SeriesMoveListResponseDto> {
  const response =
    await getApiClient().api.seriesMovesControllerGetMovesInSeries(seriesId)

  return response.data
}

/**
 * Add a move to a series
 *
 * @param data - Series-move relationship data
 * @returns Promise resolving to created series-move relationship
 */
export async function addMoveToSeries(
  data: CreateSeriesMoveDto
): Promise<SeriesMoveResponseDto> {
  const response = await getApiClient().api.seriesMovesControllerCreate(data)

  return response.data
}

/**
 * Remove a move from a series (soft delete)
 *
 * @param id - Series-move relationship ID (UUID)
 * @returns Promise resolving to void on success
 */
export async function removeMoveFromSeries(id: string): Promise<void> {
  await getApiClient().api.seriesMovesControllerDelete(id)
}
